/**
 * C5 — the connectivity state machine (module singleton).
 *
 * Owns the two APP-WIDE mechanisms that the per-endpoint circuit registry
 * deliberately does not:
 *   - Tier 0 (browser-authoritative, debounced offline): the first no-response
 *     failure — or `navigator.onLine` → false — enters a `degraded` grace
 *     window with NO UI. A healthy response or an `online` event inside the
 *     window self-heals silently; still-dead after it commits to the
 *     full-screen offline state.
 *   - Breadth (§5): ≥3 DISTINCT endpoint keys failing (5xx OR no-response)
 *     inside a sliding window proves the problem is app-wide (not one endpoint
 *     hammering), and triggers the same grace → global-takeover pipeline.
 *
 * The committed flavor is decided ONCE at commit from `navigator.onLine` +
 * time-based framing (§4), then LATCHED — never recomputed per render. Recovery
 * is a jittered API health probe + an `online` event; any healthy response tears
 * the boundary down and runs the recovery hooks (react-query invalidate).
 *
 * A response — even a 5xx or a 4xx — is proof of life, so it can never drive the
 * OFFLINE screen; a 4xx additionally gates nothing anywhere (the C2 principle).
 *
 * #239c rules:
 *   R1 — one failure never flips the app; an immediate health probe decides.
 *   R2 — timeout is not offline: `transport_timeout` with healthy probe → 'slow'.
 *   R4 — deploy resets are decided by R1's probe.
 *   R5 — while unreachable/slow a recovery probe is ALWAYS scheduled; Retry
 *         clears + refetches; if they fail the probe cycle restarts.
 *   R6 — browser events never clear; they start a probe.
 */
import { fetchWithTimeout } from '@/api/fetchWithTimeout';
import { anchorNow, outageFraming, sampleServerDate, setServerClockOffsetForTest } from './serverClock';

export type ConnectivityStatus = 'online' | 'degraded' | 'offline' | 'outage' | 'maintenance';
export type OutageFraming = 'server_issues' | 'maintenance' | null;
export type UnreachableKind = 'unreachable' | 'slow' | null;

/** R1 (#239c): short timeout for the immediate health probe. */
const IMMEDIATE_PROBE_TIMEOUT_MS = 3000;
/** #996: a recovery probe that never answers must not stop every later probe. */
export const RECOVERY_PROBE_TIMEOUT_MS = 5_000;

export interface ConnectivityConfig {
  /** Production resilience; disabled under test (mirrors the circuit breaker). */
  enabled: boolean;
  /** Debounce before committing to a screen (Tier 0 + breadth). */
  graceMs: number;
  /** Distinct failing keys inside the window that prove an app-wide problem. */
  breadthThreshold: number;
  /** Sliding window for the breadth count. */
  breadthWindowMs: number;
  /** Recovery probe backoff ladder (ms). */
  probeScheduleMs: number[];
  probeCapMs: number;
  /** Jitter on each probe — load-bearing: avoids a thundering herd on recovery. */
  probeJitterRatio: number;
  /** Endpoint probed for recovery. */
  healthUrl: string;
  /** Test seam: pin the server-clock offset used to anchor framing. */
  serverClockOffsetForTest?: number;
}

// #1843: the API's own health route. The root `/health` is served by the CDN on
// staging and production (the SPA's index.html), so it says nothing about the API.
const DEFAULT_HEALTH_URL = `${import.meta.env.VITE_API_URL || ''}/api/health/`;

let config: ConnectivityConfig = {
  enabled: true,
  graceMs: 4000,
  breadthThreshold: 3,
  breadthWindowMs: 30000,
  probeScheduleMs: [5000, 15000, 45000, 120000],
  probeCapMs: 300000,
  probeJitterRatio: 0.25,
  healthUrl: DEFAULT_HEALTH_URL,
};

export function configureConnectivity(overrides: Partial<ConnectivityConfig>): void {
  config = { ...config, ...overrides };
  if (overrides.serverClockOffsetForTest !== undefined) {
    setServerClockOffsetForTest(overrides.serverClockOffsetForTest);
  }
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let status: ConnectivityStatus = 'online';
let latchedFraming: OutageFraming = null;
let latchedAnchorMs: number | null = null;
let graceTimer: ReturnType<typeof setTimeout> | null = null;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probeLevel = 0;
// Bumped whenever an outage episode ends or a new one commits. An in-flight
// health probe from a stale episode must never tear down a newer one — it
// checks this token when its fetch resolves.
let probeGeneration = 0;
const breadthKeys = new Map<string, number>(); // key → last-failed epoch ms
// #1843: true while the current degraded episode includes a server-error (5xx)
// response. The health route answers without touching the database, so a
// healthy probe says nothing about a failing endpoint; only an episode made of
// no-response failures is cancelled by it.
let serverErrorSeen = false;

// #239c: unreachable kind — 'unreachable' | 'slow' | null. While non-null,
// onlineManager.setOnline(false) pauses TanStack queries and the loading UI
// shows the inline UnreachableState with copy matching the kind.
let unreachableKind: UnreachableKind = null;

// Subscribers (React useSyncExternalStore) + a cached, referentially-stable
// snapshot so unchanged reads don't churn.
const subscribers = new Set<() => void>();
let snapshot: {
  status: ConnectivityStatus;
  framing: OutageFraming;
  unreachable: boolean;
  unreachableKind: UnreachableKind;
} = {
  status,
  framing: null,
  unreachable: false,
  unreachableKind: null,
};

const recoveryHooks = new Set<() => void>();

function key(method: string, url: string): string {
  return `${method.toUpperCase()} ${url.split('#')[0]?.split('?')[0] ?? url}`;
}

function emit(): void {
  snapshot = {
    status,
    framing: latchedFraming,
    unreachable: unreachableKind !== null,
    unreachableKind,
  };
  for (const cb of subscribers) cb();
}

function setStatus(next: ConnectivityStatus): void {
  if (status === next) return;
  status = next;
  emit();
}

function clearGrace(): void {
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
}

function clearProbe(): void {
  if (probeTimer) {
    clearTimeout(probeTimer);
    probeTimer = null;
  }
}

function isCommitted(): boolean {
  return status === 'offline' || status === 'outage' || status === 'maintenance';
}

function runRecoveryHooks(): void {
  for (const hook of recoveryHooks) {
    try {
      hook();
    } catch (err) {
      console.error('[connectivity] recovery hook threw', err);
    }
  }
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

function enterDegraded(): void {
  if (!config.enabled) return;
  if (status !== 'online') return; // already degraded or committed
  setStatus('degraded');
  clearGrace();
  graceTimer = setTimeout(commit, config.graceMs);
}

function commit(): void {
  graceTimer = null;
  probeGeneration += 1; // a fresh outage episode owns the recovery probing
  // We only reach here if nothing healed us during the grace window
  // (`recover()` clears the timer). Decide the flavor ONCE and latch it.
  const anchor = anchorNow();
  latchedAnchorMs = anchor;
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline) {
    latchedFraming = null; // offline has no time-based framing
    setStatus('offline'); // latchedFraming is set first, so the emitted snapshot carries it
  } else {
    // SEAM: Phase 2/3 (canary.txt reachability, status.json override) refine this
    // branch. Phase 1 uses time-based framing only.
    const framing = outageFraming(anchor);
    latchedFraming = framing;
    setStatus(framing === 'server_issues' ? 'outage' : 'maintenance');
  }
  scheduleProbe();
}

/**
 * #1843: the server answered a health check, so the dropped or timed-out
 * request that armed the countdown was a blip: cancel the pending takeover.
 * Does nothing once the takeover has committed (the recovery probe owns that)
 * when the episode includes a server-error response, or when the browser
 * reports itself offline. The failed request
 * keeps its own error path, and an unreachable/slow flag is left as it is.
 */
function cancelPendingCommit(): void {
  if (status !== 'degraded' || serverErrorSeen) return;
  // The browser itself reports no network: an answer cannot have come over it.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  clearGrace();
  breadthKeys.clear();
  setStatus('online');
}

/** A healthy signal (2xx/4xx response, or a confirmed healthy API health answer). */
function recover(): void {
  if (status === 'online' && unreachableKind === null) return;
  const wasCommitted = isCommitted();
  const wasUnreachable = unreachableKind !== null;
  clearGrace();
  clearProbe();
  probeGeneration += 1; // invalidate any in-flight probe from this episode
  probeLevel = 0;
  breadthKeys.clear();
  serverErrorSeen = false;
  latchedFraming = null;
  latchedAnchorMs = null;
  // #239c: also clear the unreachable flag on recovery.
  clearUnreachable();
  setStatus('online');
  // Only resume react-query when we actually tore down a rendered boundary
  // or the unreachable inline state — a silent grace-window blip must not
  // trigger a full refetch storm.
  if (wasCommitted || wasUnreachable) runRecoveryHooks();
}

// ---------------------------------------------------------------------------
// Breadth tracking
// ---------------------------------------------------------------------------

function pruneBreadth(now: number): void {
  for (const [k, ts] of breadthKeys) {
    if (now - ts > config.breadthWindowMs) breadthKeys.delete(k);
  }
}

function markBreadthKey(k: string): void {
  const now = Date.now();
  breadthKeys.set(k, now);
  pruneBreadth(now);
  if (breadthKeys.size >= config.breadthThreshold) enterDegraded();
}

function clearBreadthKey(k: string): void {
  breadthKeys.delete(k);
}

// ---------------------------------------------------------------------------
// Public API — the interceptor's single entry point
// ---------------------------------------------------------------------------

export interface ApiResultInput {
  method: string;
  url: string;
  status?: number;
  hadResponse: boolean;
  dateHeader?: string | null;
  /** Force a response-bearing result to count as a server failure (HTML fallback). */
  treatAsServerError?: boolean;
  /** R2 (#239c): was this a timeout (classified as transport_timeout)? */
  wasTimeout?: boolean;
  /** R7 (#239c): trace id of the timed-out request (for slow report dedup). */
  traceId?: string;
  /** R7 (#239c): elapsed ms at time of timeout. */
  elapsedMs?: number;
  /** R7 (#239c): configured timeout ms for the request. */
  timeoutMs?: number;
}

export function noteApiResult(r: ApiResultInput): void {
  if (!config.enabled) return;
  const k = key(r.method, r.url);

  if (!r.hadResponse) {
    // No response — the only app-wide-capable signal. Tier 0 + breadth.
    markBreadthKey(k);
    enterDegraded();
    // R1 (#239c): do NOT call markUnreachable() directly. Run an immediate
    // health probe; only if the probe also fails does unreachable/slow flip.
    void runImmediateProbe({
      wasTimeout: r.wasTimeout ?? false,
      method: r.method,
      route: r.url.split('#')[0]?.split('?')[0] ?? r.url,
      traceId: r.traceId,
      elapsedMs: r.elapsedMs,
      timeoutMs: r.timeoutMs,
    });
    return;
  }

  // A response arrived → proof of life. Sample the server clock for framing.
  sampleServerDate(r.dateHeader);

  // #239c: any response clears the unreachable flag.
  clearUnreachable();

  const isServerError = r.treatAsServerError || (r.status !== undefined && r.status >= 500);
  if (isServerError) {
    serverErrorSeen = true;
    markBreadthKey(k); // distinct failing key for breadth; NEVER drives offline
  } else {
    // 2xx or 4xx: reachable and healthy for connectivity purposes. A 4xx gates
    // nothing (C2). Clear the key and treat as a recovery signal.
    clearBreadthKey(k);
    recover();
  }
}

// ---------------------------------------------------------------------------
// #239c: unreachable flag + TanStack onlineManager integration
// ---------------------------------------------------------------------------

let _onlineManager: { setOnline: (online: boolean) => void } | null = null;

/** Inject TanStack's onlineManager (called once from main.tsx). */
export function setOnlineManager(om: { setOnline: (online: boolean) => void }): void {
  _onlineManager = om;
}

/** R7 (#239c): trace IDs that have already had a slow report posted.
 *  Cleared on success/clearUnreachable so Retry never double-posts. */
const reportedSlowTraceIds = new Set<string>();

/** R7: slow-report callback, injected to avoid import cycle. */
let _slowReportFn: ((input: SlowReportInput) => void) | null = null;

export interface SlowReportInput {
  traceId: string;
  method: string;
  route: string;
  elapsedMs: number;
  timeoutMs: number;
  probeMs: number;
}

/** Inject the slow-report function (called once from main.tsx or the api layer). */
export function setSlowReportFn(fn: (input: SlowReportInput) => void): void {
  _slowReportFn = fn;
}

interface ImmediateProbeContext {
  wasTimeout: boolean;
  method: string;
  route: string;
  traceId?: string;
  elapsedMs?: number;
  timeoutMs?: number;
}

/** What a health request proved. */
type HealthProof = 'healthy' | 'answered' | 'no_proof';

/**
 * #1843: ask the API's health route and say what the answer proves. Only a JSON
 * body of the documented healthy shape (`{"status": "healthy", ...}`, vingapi
 * src/core/routers.py health_check) is `healthy`. A non-2xx status is
 * `answered` (something replied, nothing healthy). A 2xx that is not that JSON
 * (the CDN's index.html fallback, an empty or malformed body) is `no_proof`: it
 * cannot have come from the API. A transport failure or timeout throws.
 */
async function askApiHealth(timeoutMs: number): Promise<HealthProof> {
  const res = await fetchWithTimeout(config.healthUrl, { cache: 'no-store' }, { timeoutMs });
  if (!res.ok) return 'answered';
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object' && (body as { status?: unknown }).status === 'healthy') {
      return 'healthy';
    }
  } catch {
    /* not JSON */
  }
  return 'no_proof';
}

/**
 * R1 (#239c): immediate health probe after a no-response failure. Uses
 * `cache: 'no-store'` and a short timeout (~3 s). If the probe answers (any
 * HTTP status), the failed request keeps its normal path. If the probe also
 * fails, `markUnreachable()` runs with the appropriate kind.
 *
 * R2: a timeout (`wasTimeout`) whose probe is healthy → 'slow' (never
 * "check your internet").
 *
 * R7: when slow, posts ONE slow report (deduped by traceId).
 */
async function runImmediateProbe(ctx: ImmediateProbeContext): Promise<void> {
  const gen = probeGeneration;
  const probeStart = Date.now();
  try {
    const proof = await askApiHealth(IMMEDIATE_PROBE_TIMEOUT_MS);
    if (gen !== probeGeneration) return; // stale
    const probeMs = Date.now() - probeStart;
    if (proof === 'no_proof') throw new Error('health answer is not the API');
    // Probe got an API/gateway response (any HTTP status = reachable).
    // #1843: only a healthy API answer cancels the countdown, for BOTH a dropped
    // and a timed-out request; a bare status (503) leaves it running.
    if (proof === 'healthy') cancelPendingCommit();
    if (ctx.wasTimeout) {
      // R2: timeout with healthy probe → 'slow', not 'unreachable'
      markUnreachable('slow');
      // R7: post one slow report per trace id
      if (ctx.traceId && _slowReportFn && !reportedSlowTraceIds.has(ctx.traceId)) {
        reportedSlowTraceIds.add(ctx.traceId);
        _slowReportFn({
          traceId: ctx.traceId,
          method: ctx.method,
          route: ctx.route,
          elapsedMs: Math.round(ctx.elapsedMs ?? 0),
          timeoutMs: Math.round(ctx.timeoutMs ?? 0),
          probeMs: Math.round(probeMs),
        });
      }
    }
    // If not a timeout, the probe is healthy: the single failed request keeps
    // its normal error path — do NOT flip unreachable. (R1)
    return;
  } catch {
    // Probe also failed — mark unreachable.
  }
  if (gen !== probeGeneration) return;
  // R7: unreachable state posts NOTHING (the post could not get through).
  markUnreachable('unreachable');
}

/**
 * Mark the app as unreachable/slow. Called after the immediate health probe
 * confirms the failure is real (R1), or on a timeout with healthy probe (R2).
 * Sets TanStack's onlineManager offline (pausing queries with
 * `networkMode: 'online'`) and sets the unreachable flag so the UI can render
 * the inline retry state.
 *
 * R5: always schedules a recovery probe after flipping.
 */
export function markUnreachable(kind: 'unreachable' | 'slow' = 'unreachable'): void {
  if (unreachableKind === kind) return;
  unreachableKind = kind;
  _onlineManager?.setOnline(false);
  emit();
  // R5 (#239c): always schedule a recovery probe while unreachable/slow.
  scheduleUnreachableProbe();
}

/**
 * Clear the unreachable flag and restore TanStack's onlineManager to online.
 * Called on any successful response, a successful recovery probe, or a manual
 * retry from the UnreachableState component.
 */
export function clearUnreachable(): void {
  if (unreachableKind === null) return;
  unreachableKind = null;
  reportedSlowTraceIds.clear(); // R7: allow fresh reports after recovery
  _onlineManager?.setOnline(true);
  emit();
}

// ---------------------------------------------------------------------------
// Recovery probe (jittered API health probe)
// ---------------------------------------------------------------------------

function probeBackoff(level: number): number {
  const ladder = config.probeScheduleMs;
  const base = level < ladder.length ? ladder[level]! : config.probeCapMs;
  const capped = Math.min(base, config.probeCapMs);
  return capped + capped * config.probeJitterRatio * Math.random();
}

function scheduleProbe(): void {
  clearProbe();
  const gen = probeGeneration;
  const delay = probeBackoff(probeLevel);
  probeTimer = setTimeout(() => void runProbe(gen), delay);
}

/**
 * R5 (#239c): schedule a recovery probe for the unreachable/slow state.
 * Unlike the C5 committed-state probe, this runs even when the C5 status is
 * still 'online' (the unreachable flag is a lighter-weight overlay).
 */
function scheduleUnreachableProbe(): void {
  if (probeTimer !== null) return; // already scheduled by C5 commit or a previous call
  scheduleProbe();
}

async function runProbe(gen: number): Promise<void> {
  probeTimer = null;
  if (gen !== probeGeneration) return; // stale episode — abandon
  probeLevel += 1;
  try {
    const proof = await askApiHealth(RECOVERY_PROBE_TIMEOUT_MS);
    if (gen !== probeGeneration) return; // episode ended while the probe was in flight
    if (proof === 'healthy') {
      recover();
      return;
    }
  } catch {
    /* still unreachable */
  }
  // R5 (#239c): reschedule if still committed OR still unreachable/slow.
  if (gen === probeGeneration && (isCommitted() || unreachableKind !== null)) scheduleProbe();
}

// ---------------------------------------------------------------------------
// Browser events
// ---------------------------------------------------------------------------

function onOnline(): void {
  if (!config.enabled) return;
  if (status === 'degraded') {
    recover(); // silent self-heal inside the grace window
    return;
  }
  if (isCommitted()) {
    // The browser thinks it's back — probe immediately rather than waiting.
    clearProbe();
    void runProbe(probeGeneration);
    return;
  }
  // R6 (#239c): while unreachable/slow, an online event starts a probe but
  // NEVER directly clears the flag. Only a successful probe clears.
  if (unreachableKind !== null) {
    clearProbe();
    void runProbe(probeGeneration);
  }
}

function onOffline(): void {
  if (!config.enabled) return;
  enterDegraded();
  // R6 (#239c): an offline event can START a probe for the unreachable flag,
  // but never directly clears it.
  if (unreachableKind === null) {
    // The no-response path in noteApiResult will handle the immediate probe.
    // Nothing extra needed here; enterDegraded handles the C5 path.
  }
}

/** Manual "Try again" (R5 #239c): clear unreachable, resume queries, and
 *  start fresh. If the refetched queries fail again, the probe cycle restarts
 *  through the normal noteApiResult → runImmediateProbe path. */
export function triggerRecoveryProbe(): void {
  if (isCommitted()) {
    clearProbe();
    void runProbe(probeGeneration);
    return;
  }
  // R5: Retry works even when only unreachable/slow (not C5 committed).
  if (unreachableKind !== null) {
    clearUnreachable();
    // Recovery hooks resume paused queries; if they fail again, the
    // interceptor's noteApiResult → immediate probe will re-flip.
    runRecoveryHooks();
  }
}

let listenersInstalled = false;

/** Idempotent: attach the online/offline listeners. Called from main.tsx. */
export function installConnectivityMonitor(): void {
  if (listenersInstalled) return;
  if (typeof window === 'undefined') return;
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  listenersInstalled = true;
}

// ---------------------------------------------------------------------------
// React + recovery-hook wiring
// ---------------------------------------------------------------------------

export function subscribeConnectivity(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

export function getConnectivitySnapshot(): {
  status: ConnectivityStatus;
  framing: OutageFraming;
  unreachable: boolean;
  unreachableKind: UnreachableKind;
} {
  return snapshot;
}

/** Register a callback to run when the boundary tears down (e.g. queryClient.invalidateQueries). */
export function registerRecoveryHook(hook: () => void): () => void {
  recoveryHooks.add(hook);
  return () => {
    recoveryHooks.delete(hook);
  };
}

// ---------------------------------------------------------------------------
// Test / prod-toggle helpers
// ---------------------------------------------------------------------------

export function getStatus(): ConnectivityStatus {
  return status;
}

export function getLatchedAnchorMs(): number | null {
  return latchedAnchorMs;
}

/** Expose probe timer presence for R5 assertions. */
export function hasScheduledProbe(): boolean {
  return probeTimer !== null;
}

export function resetConnectivity(): void {
  clearGrace();
  clearProbe();
  probeGeneration += 1; // invalidate any in-flight probe across a reset
  probeLevel = 0;
  breadthKeys.clear();
  latchedFraming = null;
  latchedAnchorMs = null;
  unreachableKind = null;
  serverErrorSeen = false;
  reportedSlowTraceIds.clear(); // R7
  status = 'online';
  snapshot = { status: 'online', framing: null, unreachable: false, unreachableKind: null };
  // Restore onlineManager to online on reset (tests, logout, etc.)
  _onlineManager?.setOnline(true);
}
